import type { RoomSummaryDto } from '@room-mates/shared';
import { type ReactElement, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { useQuickFilter } from '../../shared/hooks/useQuickFilter.js';
import {
  Badge,
  Button,
  Card,
  ConfirmButton,
  EmptyState,
  ErrorAlert,
  FilterInput,
  LoadingBlock,
  PageHeader,
} from '../../shared/ui/index.js';
import { useAllRooms, useDeleteRoom, useHouses } from '../houses/houseHooks.js';
import { RoomDialog } from '../houses/RoomDialog.js';

/**
 * Every room, across every house.
 *
 * The house pages are for working on one building; this is for the moment you
 * know the room's name but not which house it is in.
 */
export function RoomsPage(): ReactElement {
  const t = useTranslation();
  const rooms = useAllRooms();
  const houses = useHouses();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RoomSummaryDto | null>(null);

  const filter = useQuickFilter(rooms.data ?? [], (room) => [room.name, room.houseName]);
  const hasHouses = (houses.data ?? []).length > 0;

  return (
    <div className="stack stack--loose">
      <PageHeader
        title={t.roomsPage.title}
        subtitle={t.roomsPage.subtitle}
        actions={
          <Button disabled={!hasHouses} onClick={() => setCreating(true)}>
            {t.roomsPage.addRoom}
          </Button>
        }
      />

      <ErrorAlert error={rooms.error} />

      {rooms.isPending ? <LoadingBlock /> : null}

      {rooms.data !== undefined && rooms.data.length > 0 ? (
        <FilterInput
          label={t.roomsPage.filterLabel}
          placeholder={t.roomsPage.filterPlaceholder}
          value={filter.query}
          matchCount={filter.matchCount}
          totalCount={filter.totalCount}
          onChange={filter.setQuery}
        />
      ) : null}

      {rooms.data?.length === 0 ? (
        hasHouses ? (
          <EmptyState
            title={t.roomsPage.noRoomsTitle}
            description={t.roomsPage.noRoomsDescription}
            action={<Button onClick={() => setCreating(true)}>{t.roomsPage.addRoom}</Button>}
          />
        ) : (
          <EmptyState
            title={t.roomsPage.noHousesTitle}
            description={t.roomsPage.noHousesDescription}
            action={
              <Link to="/houses">
                <Button>{t.roomsPage.goToHouses}</Button>
              </Link>
            }
          />
        )
      ) : null}

      {filter.isFiltering && filter.matchCount === 0 ? (
        <EmptyState
          title={t.roomsPage.noMatchesTitle}
          description={t.roomsPage.noMatchesForQuery(filter.query)}
          action={
            <Button variant="secondary" onClick={() => filter.setQuery('')}>
              {t.common.clearFilter}
            </Button>
          }
        />
      ) : null}

      {filter.matchCount > 0 ? (
        <div className="grid">
          {filter.results.map((room) => (
            <RoomCardSummary key={room.id} room={room} onEdit={() => setEditing(room)} />
          ))}
        </div>
      ) : null}

      {creating ? <RoomDialog onClose={() => setCreating(false)} /> : null}
      {editing !== null ? <RoomDialog room={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

/**
 * One room in the flat list.
 *
 * It is its own component because deleting needs a hook bound to the room's
 * house, and hooks cannot be called from inside a loop.
 */
function RoomCardSummary({
  room,
  onEdit,
}: {
  room: RoomSummaryDto;
  onEdit: () => void;
}): ReactElement {
  const t = useTranslation();
  const deleteRoom = useDeleteRoom(room.houseId);
  const overCapacity = room.sleepingPlaces > room.maxCapacity;

  return (
    <Card>
      <div className="stack stack--tight">
        <div className="row row--between">
          <h2 className="card__title">{room.name}</h2>
          {overCapacity ? (
            <Badge tone="warning">
              {t.roomCard.placesCapacity(room.sleepingPlaces, room.maxCapacity)}
            </Badge>
          ) : null}
        </div>

        <p className="card__meta">
          <Link to={`/houses/${room.houseId}`}>{room.houseName}</Link>
        </p>
        <p className="card__meta">
          {t.roomsPage.holdsUpTo(t.count.people(room.maxCapacity))} · {t.count.beds(room.bedCount)}{' '}
          · {t.count.places(room.sleepingPlaces)}
        </p>

        <ErrorAlert error={deleteRoom.error} />

        <div className="row row--between">
          <Link to={`/houses/${room.houseId}`}>{t.roomsPage.manageItsBeds}</Link>
          <div className="row">
            <Button small variant="secondary" onClick={onEdit}>
              {t.common.edit}
            </Button>
            <ConfirmButton
              small
              label={t.common.delete}
              onConfirm={() => deleteRoom.mutate(room.id)}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
